import React from "react";
import { Droplets, Sun, Shirt } from "lucide-react";
import { BottomSheet } from "../ui/bottom-sheet";

const CareSection = ({ 
  title, 
  items, 
  icon: Icon
}: { 
  title: string; 
  items: string[]; 
  icon: React.ElementType;
}) => (
  <div className="relative bg-white/50 backdrop-blur-sm rounded-xl p-5">
    <div className="flex items-center gap-3 mb-3">
      <Icon className="w-5 h-5 text-primary" />
      <h3 className="text-lg font-cinzel font-bold text-primary">{title}</h3>
    </div>
    <ul className="space-y-2 text-muted-foreground">
      {items.map((item, index) => (
        <li
          key={index}
          className="text-sm leading-relaxed flex items-start gap-2"
        >
          <span className="text-primary text-xs mt-1.5">•</span>
          {item}
        </li>
      ))}
    </ul>
  </div>
);

interface ProductCareSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProductCareSheet({ isOpen, onClose }: ProductCareSheetProps) {
  const careInstructions = [
    {
      title: "General Care",
      icon: Droplets,
      items: [
        "Wash inside out with similar colors.",
        "Use cold water (max 30°C) and a gentle cycle.",
        "Avoid harsh detergents or fabric softeners."
      ]
    },
    {
      title: "Drying and Ironing",
      icon: Sun,
      items: [
        "Air dry flat to maintain shape.",
        "Avoid tumble drying to prevent shrinkage.",
        "Use a low-heat iron if needed."
      ]
    },
    {
      title: "Storage",
      icon: Shirt,
      items: [
        "Store in a cool, dry place.",
        "Fold knits to avoid stretching.",
        "Hang structured pieces to maintain form."
      ]
    }
  ];

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="px-6 pb-6">
        {/* Header Section */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold tracking-wide font-cinzel">
            Product Care
          </h2>
          <div className="w-16 h-0.5 bg-primary/20 mx-auto mt-3" />
        </div>

        {/* Introduction */}
        <div className="mb-6 text-center">
          <p className="text-sm text-muted-foreground">
            Our garments are crafted from premium cotton and delicate handmade details. 
            Follow these care instructions to ensure longevity and preserve the luxurious feel:
          </p>
        </div>

        {/* Care Instructions */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {careInstructions.map((section, index) => (
            <CareSection
              key={index}
              title={section.title}
              items={section.items}
              icon={section.icon}
            />
          ))}
        </div>

        {/* Special Care Note */}
        <div className="bg-primary/5 backdrop-blur-sm rounded-xl p-6 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            For pieces with hand-stitched 'X' details or intricate prints, we recommend hand washing for extra care.
          </p>
          <p className="text-sm font-medium text-primary">
            By following these steps, your TEL U ASEGH pieces will remain as refined and bold as the day you got them.
          </p>
        </div>
      </div>
    </BottomSheet>
  );
} 